import { createTheme } from "@mui/material/styles";
/**
 * Create theme based on mode (light or dark)
 * This function makes it easy to add new themes or modify existing ones
 */
export const createAppTheme = (mode) => {
    const isLight = mode === "light";
    const primaryTextColor = isLight ? "#1A1A1A" : "#FFFFFF";
    const secondaryTextColor = isLight ? "#666666" : "#B4B4B4";
    const paperBackground = isLight ? "#FFFFFF" : "#1A1A1A";
    const defaultBackground = isLight ? "#F5F5F7" : "#0F0F0F";
    const primaryMainColor = isLight ? "#6B5BD1" : "#8B7CE8";
    const borderColor = isLight ? "#E0E0E0" : "#333333";
    const inputBorderColor = isLight ? "#D0D0D0" : "#444444";
    return createTheme({
        palette: {
            mode,
            primary: {
                main: primaryMainColor,
                light: isLight ? "#8B7CE8" : "#A390F0",
            },
            secondary: {
                main: isLight ? "#E31B54" : "#FF3366",
                light: isLight ? "#FF3366" : "#FF5577",
            },
            background: {
                default: defaultBackground,
                paper: paperBackground, // White for light mode, dark gray for dark
            },
            text: {
                primary: primaryTextColor,
                secondary: secondaryTextColor, // Medium gray for both modes
            },
            divider: borderColor,
            error: {
                main: isLight ? "#D32F2F" : "#F44336",
            },
            warning: {
                main: isLight ? "#ED6C02" : "#FF9800",
            },
            info: {
                main: isLight ? "#0288D1" : "#29B6F6",
            },
            success: {
                main: isLight ? "#2E7D32" : "#66BB6A",
            },
        },
        typography: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            h1: {
                fontSize: "2.5rem",
                color: primaryTextColor,
            },
            h2: {
                fontSize: "2.25rem",
                color: primaryTextColor,
            },
            h3: {
                fontSize: "2rem",
                color: primaryTextColor,
            },
            h4: {
                fontSize: "1.5rem",
                color: primaryTextColor,
            },
            h5: {
                fontSize: "1.3rem",
                color: primaryTextColor,
            },
            body1: {
                fontSize: "1.125rem",
                color: primaryTextColor,
            },
            body2: {
                fontSize: "1rem",
                color: secondaryTextColor,
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: defaultBackground,
                        color: primaryTextColor,
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: paperBackground,
                        borderBottom: isLight ? "1px solid #E0E0E0" : "2px solid #8B7CE8",
                        color: primaryTextColor,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        borderRadius: "8px",
                    },
                    contained: {
                        color: "#FFFFFF",
                    },
                    outlined: {
                        color: isLight ? primaryMainColor : "#FFFFFF",
                        borderColor: inputBorderColor,
                        "&:hover": {
                            borderColor: primaryMainColor,
                            backgroundColor: isLight ? "rgba(107, 91, 209, 0.08)" : "rgba(139, 124, 232, 0.1)",
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundColor: paperBackground,
                        borderRadius: "12px",
                        border: isLight ? `1px solid ${borderColor}` : "none",
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundColor: paperBackground,
                        borderRadius: "12px",
                        border: isLight ? `1px solid ${borderColor}` : "none",
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: paperBackground,
                            "& fieldset": {
                                borderColor: inputBorderColor,
                            },
                            "&:hover fieldset": {
                                borderColor: primaryMainColor,
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: primaryMainColor,
                            },
                        },
                        "& .MuiInputLabel-root": {
                            color: secondaryTextColor,
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderBottom: isLight ? "1px solid #E0E0E0" : "1px solid #333333",
                        color: primaryTextColor,
                    },
                    head: {
                        fontWeight: 600,
                        backgroundColor: isLight ? defaultBackground : paperBackground,
                        color: primaryTextColor,
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: "8px",
                    },
                    outlined: {
                        borderColor: inputBorderColor,
                        color: primaryTextColor,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        color: secondaryTextColor,
                        "&.Mui-selected": {
                            color: isLight ? primaryMainColor : "#FFFFFF",
                        },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        backgroundColor: primaryMainColor,
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        backgroundColor: paperBackground,
                        border: isLight ? `1px solid ${borderColor}` : "none",
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        color: primaryTextColor,
                        "&:hover": {
                            backgroundColor: isLight ? "#F5F5F7" : "#2A2A2A",
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundColor: paperBackground,
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: paperBackground,
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: { borderColor },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: "8px",
                    },
                    standardInfo: {
                        backgroundColor: isLight ? "#E3F2FD" : "#1A2633",
                        color: isLight ? "#0D47A1" : "#90CAF9",
                    },
                    standardSuccess: {
                        backgroundColor: isLight ? "#E8F5E9" : "#1B2E1F",
                        color: isLight ? "#1B5E20" : "#81C784",
                    },
                    standardWarning: {
                        backgroundColor: isLight ? "#FFF3E0" : "#332A1C",
                        color: isLight ? "#E65100" : "#FFB74D",
                    },
                    standardError: {
                        backgroundColor: isLight ? "#FFEBEE" : "#331C1C",
                        color: isLight ? "#C62828" : "#EF5350",
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        backgroundColor: borderColor,
                        borderRadius: "4px",
                    },
                    bar: {
                        backgroundColor: primaryMainColor,
                    },
                },
            },
            // DataGrid dark theme
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        border: `1px solid ${borderColor}`,
                        color: primaryTextColor,
                        "& .MuiDataGrid-cell": {
                            borderBottom: `1px solid ${borderColor}`,
                            color: primaryTextColor,
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: isLight ? defaultBackground : paperBackground,
                            borderBottom: `1px solid ${inputBorderColor}`,
                            color: primaryTextColor,
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: 600,
                            color: primaryTextColor,
                        },
                        "& .MuiDataGrid-row": {
                            "&:hover": {
                                backgroundColor: isLight ? "#F5F5F7" : "#2A2A2A",
                            },
                            "&.Mui-selected": {
                                backgroundColor: isLight ? "#E8E4F3" : "#2A2A2A",
                                "&:hover": {
                                    backgroundColor: isLight ? "#DDD6F0" : "#333333",
                                },
                            },
                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: `1px solid ${borderColor}`,
                            backgroundColor: isLight ? defaultBackground : paperBackground,
                        },
                        "& .MuiTablePagination-root": {
                            color: primaryTextColor,
                        },
                        "& .MuiCheckbox-root": {
                            color: isLight ? "#999999" : "#666666",
                            "&.Mui-checked": {
                                color: primaryMainColor,
                            },
                        },
                        "& .MuiDataGrid-iconSeparator": {
                            color: inputBorderColor,
                        },
                    },
                },
            },
        },
    });
};
// Export default dark theme for backward compatibility
const darkTheme = createAppTheme("dark");
export default darkTheme;
